import { useMemo, useState, type FormEvent } from "react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpDown,
  ArrowUpRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Ellipsis,
  FolderKanban,
  GripVertical,
  LayoutGrid,
  List,
  Mail,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  Users,
} from "lucide-react";
import {
  downloadFile,
  formatDate,
  formatMoney,
  toCSV,
  uid,
  type Contact,
  type Deal,
  type Project,
  type Task,
} from "../../../core/src";
import type { PageProps } from "../types";
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  IconButton,
  Metric,
  Modal,
  PageHeader,
  Panel,
  Progress,
} from "../components/ui";

const localToday = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export function ContactsPage({ state, mutate, notify, navigate }: PageProps) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<string[]>([]);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [open, setOpen] = useState(false);
  const [remove, setRemove] = useState<string | null>(null);
  const filtered = useMemo(
    () =>
      state.contacts
        .filter(
          (c) =>
            (filter === "all" || c.status === filter) &&
            `${c.name} ${c.email} ${c.company}`
              .toLowerCase()
              .includes(query.toLowerCase()),
        )
        .sort(
          (a, b) => (sort === "asc" ? 1 : -1) * a.name.localeCompare(b.name),
        ),
    [state.contacts, filter, query, sort],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / 8));
  const current = Math.min(page, pages);
  const rows = filtered.slice((current - 1) * 8, current * 8);
  const begin = (contact: Contact | null) => {
    setEditing(contact);
    setOpen(true);
  };
  const save = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const contact: Contact = {
      id: editing?.id || uid("contact"),
      name: String(data.get("name")).trim(),
      email: String(data.get("email")).trim(),
      company: String(data.get("company")).trim(),
      role: String(data.get("role")).trim(),
      country: String(data.get("country")).trim(),
      status: String(data.get("status")) as Contact["status"],
      revenue: editing?.revenue || 0,
      color: editing?.color || "#E9E4D9",
    };
    if (
      !mutate((s) => ({
        ...s,
        contacts: editing
          ? s.contacts.map((c) => (c.id === contact.id ? contact : c))
          : [contact, ...s.contacts],
      }))
    )
      return;
    notify(editing ? "Contact updated." : "A new relationship, ready to grow.");
    setOpen(false);
  };
  const exportContacts = () => {
    const data = selection.length
      ? state.contacts.filter((c) => selection.includes(c.id))
      : filtered;
    downloadFile(
      "elyxir-contacts.csv",
      toCSV(
        ["Name", "Email", "Company", "Role", "Country", "Status", "Revenue"],
        data.map((c) => [
          c.name,
          c.email,
          c.company,
          c.role,
          c.country,
          c.status,
          c.revenue,
        ]),
      ),
      "text/csv",
    );
    notify(`${data.length} contacts exported.`);
  };
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="RELATIONSHIPS THAT MATTER"
        title="Your people, connected."
        description="Build stronger relationships, one conversation at a time."
        actions={
          <>
            <Button onClick={exportContacts}>
              <Download size={16} />
              Export
            </Button>
            <Button variant="primary" onClick={() => begin(null)}>
              <Plus size={16} />
              Add contact
            </Button>
          </>
        }
      />
      <div className="stat-grid three">
        <Metric
          featured
          label="Total contacts"
          value={String(state.contacts.length)}
          note="In your address book"
          icon={<Users size={18} />}
        />
        <Metric
          label="Active relationships"
          value={String(
            state.contacts.filter((c) => c.status === "active").length,
          )}
          note="Ready for the next conversation"
        />
        <Metric
          label="Customer value"
          value={formatMoney(
            state.contacts.reduce((s, c) => s + c.revenue, 0),
            state.settings.currency,
          )}
          note="Recorded lifetime revenue"
        />
      </div>
      <Panel>
        <div className="toolbar">
          <div className="filter-tabs">
            {["all", "active", "inactive"].map((value) => (
              <button
                key={value}
                className={filter === value ? "active" : ""}
                onClick={() => {
                  setFilter(value);
                  setPage(1);
                }}
              >
                {value === "all" ? "All contacts" : value}
                <span>
                  {value === "all"
                    ? state.contacts.length
                    : state.contacts.filter((c) => c.status === value).length}
                </span>
              </button>
            ))}
          </div>
          <label className="search-input">
            <Search size={16} />
            <input
              placeholder="Search your contacts…"
              aria-label="Search contacts"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>
        {selection.length > 0 && (
          <div className="selection-bar">
            <span>{selection.length} selected</span>
            <Button variant="ghost" onClick={exportContacts}>
              Export selected
            </Button>
            <Button variant="ghost" onClick={() => setSelection([])}>
              Clear
            </Button>
          </div>
        )}
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th className="check-cell">
                  <input
                    type="checkbox"
                    aria-label="Select visible contacts"
                    checked={
                      rows.length > 0 &&
                      rows.every((c) => selection.includes(c.id))
                    }
                    onChange={(e) =>
                      setSelection(
                        e.target.checked
                          ? [
                              ...new Set([
                                ...selection,
                                ...rows.map((c) => c.id),
                              ]),
                            ]
                          : selection.filter(
                              (id) => !rows.some((c) => c.id === id),
                            ),
                      )
                    }
                  />
                </th>
                <th>
                  <button
                    className="table-sort"
                    onClick={() => setSort(sort === "asc" ? "desc" : "asc")}
                  >
                    Name <ArrowUpDown size={13} />
                  </button>
                </th>
                <th>Company</th>
                <th>Status</th>
                <th>Country</th>
                <th>Value</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${c.name}`}
                      checked={selection.includes(c.id)}
                      onChange={(e) =>
                        setSelection(
                          e.target.checked
                            ? [...selection, c.id]
                            : selection.filter((id) => id !== c.id),
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      className="person-cell row-button"
                      onClick={() => begin(c)}
                    >
                      <Avatar name={c.name} color={c.color} />
                      <span>
                        <span className="record-title">{c.name}</span>
                        <span className="record-subtitle">{c.email}</span>
                      </span>
                    </button>
                  </td>
                  <td>
                    <span className="record-title">{c.company}</span>
                    <span className="record-subtitle">{c.role}</span>
                  </td>
                  <td>
                    <Badge tone={c.status === "active" ? "green" : "neutral"}>
                      {c.status}
                    </Badge>
                  </td>
                  <td>{c.country}</td>
                  <td className="amount">
                    {formatMoney(c.revenue, state.settings.currency)}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <IconButton
                        label={`Edit ${c.name}`}
                        onClick={() => begin(c)}
                      >
                        <Pencil size={15} />
                      </IconButton>
                      <IconButton
                        label={`Delete ${c.name}`}
                        onClick={() => setRemove(c.id)}
                      >
                        <Trash2 size={15} />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!rows.length && (
          <EmptyState
            icon={<Users />}
            title="No contacts found"
            description="Try a different search or add someone new."
            action={<Button onClick={() => begin(null)}>Add contact</Button>}
          />
        )}
        <div className="pagination">
          <span>
            Showing {filtered.length ? (current - 1) * 8 + 1 : 0}–
            {Math.min(current * 8, filtered.length)} of {filtered.length}{" "}
            contacts
          </span>
          <div className="flex gap-2">
            <IconButton
              label="Previous page"
              disabled={current === 1}
              onClick={() => setPage(current - 1)}
            >
              <ChevronLeft size={16} />
            </IconButton>
            <span>
              {current} / {pages}
            </span>
            <IconButton
              label="Next page"
              disabled={current === pages}
              onClick={() => setPage(current + 1)}
            >
              <ChevronRight size={16} />
            </IconButton>
          </div>
        </div>
      </Panel>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit contact" : "Someone new to know"}
        description="Keep the details that help you build a better relationship."
      >
        <form onSubmit={save}>
          <div className="form-grid">
            <Field label="Full name">
              <input
                className="input"
                name="name"
                required
                maxLength={120}
                defaultValue={editing?.name}
              />
            </Field>
            <Field label="Email address">
              <input
                className="input"
                name="email"
                type="email"
                maxLength={254}
                required
                defaultValue={editing?.email}
              />
            </Field>
            <Field label="Company">
              <input
                className="input"
                name="company"
                maxLength={500}
                required
                defaultValue={editing?.company}
              />
            </Field>
            <Field label="Role">
              <input
                className="input"
                name="role"
                maxLength={500}
                required
                defaultValue={editing?.role}
              />
            </Field>
            <Field label="Country">
              <input
                className="input"
                name="country"
                maxLength={500}
                required
                defaultValue={editing?.country}
              />
            </Field>
            <Field label="Status">
              <select
                className="select"
                name="status"
                defaultValue={editing?.status || "active"}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
          </div>
          <div className="form-actions">
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary">
              {editing ? "Save changes" : "Create contact"}
            </Button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={!!remove}
        onClose={() => setRemove(null)}
        description="This contact and any linked sales opportunities will be removed."
        onConfirm={() => {
          if (
            !mutate((s) => ({
              ...s,
              contacts: s.contacts.filter((c) => c.id !== remove),
              deals: s.deals.filter((d) => d.contactId !== remove),
            }))
          )
            return false;
          setSelection(selection.filter((id) => id !== remove));
          notify("Contact removed.");
          return true;
        }}
      />
    </div>
  );
}

const dealStages = [
  { id: "lead", label: "New leads", tone: "neutral" },
  { id: "qualified", label: "Qualified", tone: "blue" },
  { id: "proposal", label: "Proposal", tone: "amber" },
  { id: "won", label: "Won", tone: "green" },
] as const;
export function PipelinePage({ state, mutate, notify }: PageProps) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Deal | null>(null);
  const [query, setQuery] = useState("");
  const [showLost, setShowLost] = useState(false);
  const [remove, setRemove] = useState<string | null>(null);
  const save = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const deal: Deal = {
      id: editing?.id || uid("deal"),
      title: String(f.get("title")).trim(),
      contactId: String(f.get("contactId")),
      value: Number(f.get("value")),
      stage: String(f.get("stage")) as Deal["stage"],
      probability: Number(f.get("probability")),
      closeDate: String(f.get("closeDate")),
    };
    deal.probability =
      deal.stage === "won" ? 100 : deal.stage === "lost" ? 0 : deal.probability;
    if (
      !mutate((s) => ({
        ...s,
        deals: editing
          ? s.deals.map((d) => (d.id === deal.id ? deal : d))
          : [deal, ...s.deals],
      }))
    )
      return;
    setOpen(false);
    notify(editing ? "Opportunity updated." : "New opportunity added.");
  };
  const move = (id: string, stage: Deal["stage"]) => {
    if (
      !mutate((s) => ({
        ...s,
        deals: s.deals.map((d) =>
          d.id === id
            ? {
                ...d,
                stage,
                probability:
                  stage === "won" ? 100 : stage === "lost" ? 0 : d.probability,
              }
            : d,
        ),
      }))
    )
      return;
    notify("Opportunity moved.");
  };
  const stages = showLost
    ? [...dealStages, { id: "lost", label: "Lost", tone: "red" } as const]
    : dealStages;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="TURN POSSIBILITY INTO PROGRESS"
        title="Sales pipeline"
        description="A clear path from your first conversation to your next win."
        actions={
          <Button
            variant="primary"
            disabled={!state.contacts.length}
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus size={16} />
            New opportunity
          </Button>
        }
      />
      <div className="pipeline-summary">
        <div>
          <span>Pipeline value</span>
          <strong>
            {formatMoney(
              state.deals
                .filter((d) => !["won", "lost"].includes(d.stage))
                .reduce((s, d) => s + d.value, 0),
              state.settings.currency,
            )}
          </strong>
        </div>
        <div>
          <span>Weighted forecast</span>
          <strong>
            {formatMoney(
              state.deals
                .filter((d) => !["won", "lost"].includes(d.stage))
                .reduce((s, d) => s + (d.value * d.probability) / 100, 0),
              state.settings.currency,
            )}
          </strong>
        </div>
        <div>
          <span>Won business</span>
          <strong>
            {formatMoney(
              state.deals
                .filter((d) => d.stage === "won")
                .reduce((s, d) => s + d.value, 0),
              state.settings.currency,
            )}
          </strong>
        </div>
      </div>
      <div className="toolbar">
        <label className="search-input">
          <Search size={16} />
          <input
            aria-label="Search opportunities"
            placeholder="Find an opportunity…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showLost}
            onChange={(e) => setShowLost(e.target.checked)}
          />
          Show lost opportunities
        </label>
      </div>
      <div
        className="kanban-board"
        style={{
          gridTemplateColumns: `repeat(${stages.length}, minmax(245px, 1fr))`,
        }}
      >
        {stages.map((stage) => {
          const deals = state.deals.filter(
            (d) =>
              d.stage === stage.id &&
              d.title.toLowerCase().includes(query.toLowerCase()),
          );
          return (
            <section
              className="kanban-column"
              key={stage.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData("elyxir/deal");
                if (state.deals.some((d) => d.id === id)) move(id, stage.id);
              }}
            >
              <div className="kanban-heading">
                <h2>
                  <span className={`stage-dot stage-${stage.id}`} />
                  {stage.label}
                  <span>{deals.length}</span>
                </h2>
                <strong>
                  {formatMoney(
                    deals.reduce((s, d) => s + d.value, 0),
                    state.settings.currency,
                  )}
                </strong>
              </div>
              {deals.map((deal) => {
                const contact = state.contacts.find(
                  (c) => c.id === deal.contactId,
                );
                return (
                  <article
                    className="deal-card"
                    key={deal.id}
                    draggable
                    onDragStart={(e) =>
                      e.dataTransfer.setData("elyxir/deal", deal.id)
                    }
                  >
                    <div className="flex-between">
                      <span className="card-eyebrow">
                        {contact?.company || "Opportunity"}
                      </span>
                      <IconButton
                        label={`Edit ${deal.title}`}
                        onClick={() => {
                          setEditing(deal);
                          setOpen(true);
                        }}
                      >
                        <Ellipsis size={17} />
                      </IconButton>
                    </div>
                    <button
                      className="deal-title"
                      onClick={() => {
                        setEditing(deal);
                        setOpen(true);
                      }}
                    >
                      {deal.title}
                    </button>
                    <strong className="deal-value">
                      {formatMoney(deal.value, state.settings.currency)}
                    </strong>
                    <div className="deal-contact">
                      <Avatar
                        name={contact?.name || "Unknown contact"}
                        color={contact?.color}
                        size="sm"
                      />
                      <span>{contact?.name || "Unknown contact"}</span>
                    </div>
                    <div className="deal-footer">
                      <span>{formatDate(deal.closeDate)}</span>
                      <span>{deal.probability}% chance</span>
                    </div>
                    <select
                      className="select stage-select"
                      aria-label={`Move ${deal.title} to stage`}
                      value={deal.stage}
                      onChange={(e) =>
                        move(deal.id, e.target.value as Deal["stage"])
                      }
                    >
                      {[...dealStages, { id: "lost", label: "Lost" }].map(
                        (s) => (
                          <option value={s.id} key={s.id}>
                            {s.label}
                          </option>
                        ),
                      )}
                    </select>
                  </article>
                );
              })}
              {!deals.length && (
                <div className="kanban-empty">Drop an opportunity here</div>
              )}
            </section>
          );
        })}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit opportunity" : "A new possibility"}
      >
        <form onSubmit={save}>
          <div className="form-grid">
            <Field label="Opportunity name">
              <input
                className="input"
                name="title"
                maxLength={500}
                required
                defaultValue={editing?.title}
              />
            </Field>
            <Field label="Contact">
              <select
                className="select"
                name="contactId"
                defaultValue={editing?.contactId}
                required
              >
                {state.contacts.map((c) => (
                  <option value={c.id} key={c.id}>
                    {c.name} · {c.company}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Value">
              <input
                className="input"
                name="value"
                type="number"
                min="0"
                max="1000000000"
                step="0.01"
                required
                defaultValue={editing?.value || 0}
              />
            </Field>
            <Field label="Probability (%)">
              <input
                className="input"
                name="probability"
                type="number"
                min="0"
                max="100"
                required
                defaultValue={editing?.probability ?? 20}
              />
            </Field>
            <Field label="Stage">
              <select
                className="select"
                name="stage"
                defaultValue={editing?.stage || "lead"}
              >
                {[...dealStages, { id: "lost", label: "Lost" }].map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Expected close date">
              <input
                className="input"
                name="closeDate"
                type="date"
                required
                defaultValue={editing?.closeDate || localToday()}
              />
            </Field>
          </div>
          <div className="form-actions">
            {editing && (
              <Button
                variant="danger"
                onClick={() => {
                  setRemove(editing.id);
                  setOpen(false);
                }}
              >
                Delete
              </Button>
            )}
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary">
              Save opportunity
            </Button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={!!remove}
        onClose={() => setRemove(null)}
        onConfirm={() => {
          if (
            !mutate((s) => ({
              ...s,
              deals: s.deals.filter((d) => d.id !== remove),
            }))
          )
            return false;
          notify("Opportunity removed.");
          return true;
        }}
      />
    </div>
  );
}

export function ProjectsPage({ state, mutate, notify, navigate }: PageProps) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [remove, setRemove] = useState<string | null>(null);
  const projects = state.projects.filter(
    (p) =>
      (filter === "all" || p.status === filter) &&
      `${p.name} ${p.client}`.toLowerCase().includes(query.toLowerCase()),
  );
  const save = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const project: Project = {
      id: editing?.id || uid("project"),
      name: String(f.get("name")).trim(),
      client: String(f.get("client")).trim(),
      description: String(f.get("description")).trim(),
      color: String(f.get("color")),
      status: String(f.get("status")) as Project["status"],
      progress: Number(f.get("progress")),
      budget: Number(f.get("budget")),
      dueDate: String(f.get("dueDate")),
    };
    if (
      !mutate((s) => ({
        ...s,
        projects: editing
          ? s.projects.map((p) => (p.id === project.id ? project : p))
          : [project, ...s.projects],
      }))
    )
      return;
    setOpen(false);
    notify(editing ? "Project updated." : "Your next big thing starts here.");
  };
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="GOOD IDEAS DESERVE GREAT EXECUTION"
        title="Space for your next big thing."
        description="Plan thoughtfully. Build together. Bring your best work to life."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus size={16} />
            New project
          </Button>
        }
      />
      <div className="toolbar">
        <div className="filter-tabs">
          {["all", "active", "paused", "completed"].map((f) => (
            <button
              className={filter === f ? "active" : ""}
              key={f}
              onClick={() => setFilter(f)}
            >
              {f === "all" ? "All projects" : f}
            </button>
          ))}
        </div>
        <label className="search-input">
          <Search size={16} />
          <input
            placeholder="Search projects…"
            aria-label="Search projects"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      <div className="project-grid">
        {projects.map((p, i) => {
          const tasks = state.tasks.filter((t) => t.projectId === p.id);
          return (
            <article className="project-card" key={p.id}>
              <div className="project-card-top">
                <span
                  className="project-symbol"
                  style={{ background: p.color }}
                >
                  <FolderKanban size={23} />
                </span>
                <Badge
                  tone={
                    p.status === "completed"
                      ? "green"
                      : p.status === "paused"
                        ? "amber"
                        : "blue"
                  }
                >
                  {p.status}
                </Badge>
                <IconButton
                  label={`Edit ${p.name}`}
                  onClick={() => {
                    setEditing(p);
                    setOpen(true);
                  }}
                >
                  <Ellipsis size={19} />
                </IconButton>
              </div>
              <button
                className="project-card-title"
                onClick={() => {
                  setEditing(p);
                  setOpen(true);
                }}
              >
                {p.name}
              </button>
              <p className="project-client">{p.client}</p>
              <p className="project-description">{p.description}</p>
              <div className="project-progress-label">
                <span>Progress</span>
                <strong>{p.progress}%</strong>
              </div>
              <Progress value={p.progress} label={p.name} />
              <div className="project-card-details">
                <span>
                  <Check size={14} />
                  {tasks.filter((t) => t.status === "done").length}/
                  {tasks.length} tasks
                </span>
                <span>{formatMoney(p.budget, state.settings.currency)}</span>
              </div>
              <footer>
                <div className="avatar-stack">
                  {state.members.slice(i % 2, (i % 2) + 3).map((m) => (
                    <Avatar
                      name={m.name}
                      color={m.color}
                      size="sm"
                      key={m.id}
                    />
                  ))}
                </div>
                <span>Due {formatDate(p.dueDate)}</span>
                <IconButton
                  label={`View tasks for ${p.name}`}
                  onClick={() =>
                    navigate(`tasks?project=${encodeURIComponent(p.id)}`)
                  }
                >
                  <ArrowUpRight size={17} />
                </IconButton>
              </footer>
            </article>
          );
        })}
        <button
          className="project-card project-card-add"
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <span>
            <Plus size={24} />
          </span>
          <strong>Something worth building?</strong>
          <p>Give your next idea a place to grow.</p>
          <span className="text-link">
            Create a new project <ArrowRight size={15} />
          </span>
        </button>
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Project details" : "Start something great"}
        description="A clear brief makes room for better work."
      >
        <form onSubmit={save}>
          <div className="form-grid">
            <Field label="Project name">
              <input
                className="input"
                name="name"
                maxLength={200}
                required
                defaultValue={editing?.name}
              />
            </Field>
            <Field label="Client or team">
              <input
                className="input"
                name="client"
                maxLength={500}
                required
                defaultValue={editing?.client}
              />
            </Field>
            <div className="span-2">
              <Field label="Description">
                <textarea
                  className="input"
                  name="description"
                  maxLength={20000}
                  rows={3}
                  required
                  defaultValue={editing?.description}
                />
              </Field>
            </div>
            <Field label="Budget">
              <input
                className="input"
                name="budget"
                type="number"
                min="0"
                max="1000000000"
                step="0.01"
                required
                defaultValue={editing?.budget || 0}
              />
            </Field>
            <Field label="Due date">
              <input
                className="input"
                name="dueDate"
                type="date"
                required
                defaultValue={editing?.dueDate || localToday()}
              />
            </Field>
            <Field label="Status">
              <select
                className="select"
                name="status"
                defaultValue={editing?.status || "active"}
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </select>
            </Field>
            <Field label="Progress (%)">
              <input
                className="input"
                name="progress"
                type="number"
                min="0"
                max="100"
                required
                defaultValue={editing?.progress || 0}
              />
            </Field>
            <Field label="Project colour">
              <input
                className="input color-input"
                name="color"
                type="color"
                defaultValue={editing?.color || "#DBE7D4"}
              />
            </Field>
          </div>
          <div className="form-actions">
            {editing && (
              <Button
                variant="danger"
                onClick={() => {
                  setOpen(false);
                  setRemove(editing.id);
                }}
              >
                Delete
              </Button>
            )}
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" type="submit">
              {editing ? "Save changes" : "Create project"}
            </Button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={!!remove}
        onClose={() => setRemove(null)}
        description="This project and its associated tasks will be removed."
        onConfirm={() => {
          if (
            !mutate((s) => ({
              ...s,
              projects: s.projects.filter((p) => p.id !== remove),
              tasks: s.tasks.filter((t) => t.projectId !== remove),
            }))
          )
            return false;
          notify("Project removed.");
          return true;
        }}
      />
    </div>
  );
}

const taskStages = [
  { id: "todo", label: "To do" },
  { id: "progress", label: "In progress" },
  { id: "review", label: "In review" },
  { id: "done", label: "Done" },
] as const;
export function TasksPage({ state, mutate, notify }: PageProps) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);
  const [query, setQuery] = useState("");
  const [project, setProject] = useState(
    () =>
      new URLSearchParams(
        typeof window === "undefined" ? "" : window.location.hash.split("?")[1],
      ).get("project") || "all",
  );
  const [view, setView] = useState("board");
  const [remove, setRemove] = useState<string | null>(null);
  const tasks = state.tasks.filter(
    (t) =>
      (project === "all" || t.projectId === project) &&
      t.title.toLowerCase().includes(query.toLowerCase()),
  );
  const move = (id: string, status: Task["status"]) => {
    if (
      !mutate((s) => ({
        ...s,
        tasks: s.tasks.map((t) => (t.id === id ? { ...t, status } : t)),
      }))
    )
      return;
    notify(
      status === "done" ? "One more thing, beautifully done." : "Task moved.",
    );
  };
  const save = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const task: Task = {
      id: editing?.id || uid("task"),
      title: String(f.get("title")).trim(),
      description: String(f.get("description")).trim(),
      projectId: String(f.get("projectId")),
      status: String(f.get("status")) as Task["status"],
      priority: String(f.get("priority")) as Task["priority"],
      dueDate: String(f.get("dueDate")),
      assignee: String(f.get("assignee") ?? ""),
    };
    if (
      !mutate((s) => ({
        ...s,
        tasks: editing
          ? s.tasks.map((t) => (t.id === task.id ? task : t))
          : [task, ...s.tasks],
      }))
    )
      return;
    setOpen(false);
    notify(editing ? "Task updated." : "Task added to the board.");
  };
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="A LITTLE FOCUS GOES A LONG WAY"
        title="Good work, in motion."
        description="Keep the important things moving, together."
        actions={
          <Button
            variant="primary"
            disabled={!state.projects.length}
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus size={16} />
            New task
          </Button>
        }
      />
      <div className="toolbar">
        <div className="flex gap-2">
          <div className="segmented">
            <button
              aria-label="Board view"
              className={view === "board" ? "active" : ""}
              onClick={() => setView("board")}
            >
              <LayoutGrid size={15} />
              Board
            </button>
            <button
              aria-label="List view"
              className={view === "list" ? "active" : ""}
              onClick={() => setView("list")}
            >
              <List size={15} />
              List
            </button>
          </div>
          <select
            className="select compact-select"
            aria-label="Filter by project"
            value={project}
            onChange={(e) => setProject(e.target.value)}
          >
            <option value="all">All projects</option>
            {state.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <label className="search-input">
          <Search size={16} />
          <input
            placeholder="Find your focus…"
            aria-label="Search tasks"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      {view === "board" ? (
        <div className="kanban-board">
          {taskStages.map((stage) => (
            <section
              className="kanban-column"
              key={stage.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData("elyxir/task");
                if (state.tasks.some((t) => t.id === id)) move(id, stage.id);
              }}
            >
              <div className="kanban-heading">
                <h2>
                  <span className={`stage-dot stage-${stage.id}`} />
                  {stage.label}
                  <span>
                    {tasks.filter((t) => t.status === stage.id).length}
                  </span>
                </h2>
              </div>
              {tasks
                .filter((t) => t.status === stage.id)
                .map((task) => (
                  <article
                    className={`task-card ${task.status === "done" ? "task-done" : ""}`}
                    key={task.id}
                    draggable
                    onDragStart={(e) =>
                      e.dataTransfer.setData("elyxir/task", task.id)
                    }
                  >
                    <div className="flex-between">
                      <Badge
                        tone={
                          task.priority === "high"
                            ? "red"
                            : task.priority === "medium"
                              ? "amber"
                              : "neutral"
                        }
                      >
                        {task.priority}
                      </Badge>
                      <IconButton
                        label={`Edit ${task.title}`}
                        onClick={() => {
                          setEditing(task);
                          setOpen(true);
                        }}
                      >
                        <Ellipsis size={17} />
                      </IconButton>
                    </div>
                    <button
                      className="task-title"
                      onClick={() => {
                        setEditing(task);
                        setOpen(true);
                      }}
                    >
                      {task.title}
                    </button>
                    <p>{task.description}</p>
                    <span className="task-project">
                      {
                        state.projects.find((p) => p.id === task.projectId)
                          ?.name
                      }
                    </span>
                    <div className="task-footer">
                      <span>{formatDate(task.dueDate)}</span>
                      <Avatar name={task.assignee || "Unassigned"} size="sm" />
                    </div>
                    <select
                      className="select stage-select"
                      aria-label={`Move ${task.title} to status`}
                      value={task.status}
                      onChange={(e) =>
                        move(task.id, e.target.value as Task["status"])
                      }
                    >
                      {taskStages.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </article>
                ))}
              {!tasks.some((t) => t.status === stage.id) && (
                <div className="kanban-empty">
                  A little space for what’s next
                </div>
              )}
            </section>
          ))}
        </div>
      ) : (
        <Panel>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Project</th>
                  <th>Priority</th>
                  <th>Due</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <button
                        className="table-link"
                        onClick={() => {
                          setEditing(task);
                          setOpen(true);
                        }}
                      >
                        {task.title}
                      </button>
                    </td>
                    <td>
                      {
                        state.projects.find((p) => p.id === task.projectId)
                          ?.name
                      }
                    </td>
                    <td>
                      <Badge
                        tone={task.priority === "high" ? "red" : "neutral"}
                      >
                        {task.priority}
                      </Badge>
                    </td>
                    <td>{formatDate(task.dueDate)}</td>
                    <td>
                      <select
                        className="select"
                        value={task.status}
                        aria-label={`Status of ${task.title}`}
                        onChange={(e) =>
                          move(task.id, e.target.value as Task["status"])
                        }
                      >
                        {taskStages.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <IconButton
                        label={`Edit ${task.title}`}
                        onClick={() => {
                          setEditing(task);
                          setOpen(true);
                        }}
                      >
                        <Pencil size={16} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "A closer look" : "What’s the next step?"}
      >
        <form onSubmit={save}>
          <div className="form-grid">
            <div className="span-2">
              <Field label="Task title">
                <input
                  name="title"
                  className="input"
                  maxLength={500}
                  required
                  defaultValue={editing?.title}
                />
              </Field>
            </div>
            <div className="span-2">
              <Field label="Description">
                <textarea
                  className="input"
                  name="description"
                  maxLength={20000}
                  rows={3}
                  required
                  defaultValue={editing?.description}
                />
              </Field>
            </div>
            <Field label="Project">
              <select
                className="select"
                name="projectId"
                required
                defaultValue={
                  editing?.projectId ||
                  (project !== "all" ? project : undefined)
                }
              >
                {state.projects.map((p) => (
                  <option value={p.id} key={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Assignee">
              <select
                className="select"
                name="assignee"
                defaultValue={editing?.assignee ?? ""}
              >
                <option value="">Unassigned</option>
                {editing?.assignee &&
                  !state.members.some((m) => m.name === editing.assignee) && (
                    <option value={editing.assignee}>
                      {editing.assignee} (not on team)
                    </option>
                  )}
                {state.members.map((m) => (
                  <option key={m.id} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                className="select"
                name="status"
                defaultValue={editing?.status || "todo"}
              >
                {taskStages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Priority">
              <select
                className="select"
                name="priority"
                defaultValue={editing?.priority || "medium"}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </Field>
            <Field label="Due date">
              <input
                className="input"
                name="dueDate"
                type="date"
                required
                defaultValue={editing?.dueDate || localToday()}
              />
            </Field>
          </div>
          <div className="form-actions">
            {editing && (
              <Button
                variant="danger"
                onClick={() => {
                  setOpen(false);
                  setRemove(editing.id);
                }}
              >
                Delete
              </Button>
            )}
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary">
              Save task
            </Button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={!!remove}
        onClose={() => setRemove(null)}
        onConfirm={() => {
          if (
            !mutate((s) => ({
              ...s,
              tasks: s.tasks.filter((t) => t.id !== remove),
            }))
          )
            return false;
          notify("Task removed.");
          return true;
        }}
      />
    </div>
  );
}
